<template>
  <div
    class="media-carousel__media"
    :class="layout === 'narrow' ? null : 'fixedHeight'"
  >
    <responsive-image
      v-if="block.mobileImage?.src"
      :image="block.mobileImage"
      :ratio="layout === 'narrow' && !hasAvailableVideo ? 0.8 : false"
      :lazyload="false"
      class="media-carousel__image--mobile"
      :padding="layout === 'narrow' && !hasAvailableVideo ? true : false"
      min-max="200-960"
      sizes="100vw"
      @responsive-image-loaded="$emit('carousel-image-loaded', true)"
    />

    <responsive-image
      v-if="block.desktopImage?.src"
      :image="block.desktopImage"
      :ratio="layout === 'narrow' && !hasAvailableVideo ? '16:9' : false"
      class="media-carousel__image--desktop"
      :lazyload="false"
      :padding="layout === 'narrow' && !hasAvailableVideo ? true : false"
      min-max="200-2000"
      sizes="100vw"
      @responsive-image-loaded="$emit('carousel-image-loaded', true)"
    />

    <video-player
      v-if="block.videoControls?.videoEnable && (block.mobileVideo || block.desktopVideo)"
      :video="isMobile ? block.mobileVideo : block.desktopVideo"
      :video-controls="block.videoControls"
      :class="[
        !block.mobileVideo && 'mobile-hidden',
        !block.desktopVideo && 'desktop-hidden',
      ]"
      :show-overlay="
        isMobile && block.mobileVideo || !isMobile && block.desktopVideo
      "
      :image="isMobile ? block.mobileImage : block.desktopImage"
      @video-loaded="$emit('carousel-image-loaded', true)"
    />
  </div>

  <div class="media-carousel__content">
    <p
      v-if="block.subtitle"
      class="media-carousel__subtitle"
      v-html="block.subtitle"
    />

    <component
      :is="block.showTitleAsPageHeading ? 'h1' : 'p'"
      v-if="block.title"
      class="media-carousel__title"
      v-html="block.title"
    />

    <p
      v-if="block.text"
      class="body-1 media-carousel__text"
      v-html="block.text"
    />

    <!-- CTA link -->
    <btn
      v-if="block.ctaText && block.ctaUrl"
      block-class="media-carousel"
      :label="block.ctaText"
      :url="block.ctaUrl"
    />

    <!-- CTA to trigger video modal -->
    <btn
      v-else-if="block.videoControls.videoModalEnable && block.ctaText && !block.ctaUrl"
      class="media-carousel__button-video-modal"
      block-class="media-carousel"
      :label="block.ctaText"
      @click="toggleOverlay({
        component: 'video-player',
        ignoreDismissed: true,
        namespace: `video-player-overlay-${block.id}`,
      })"
    />
  </div>
</template>

<script>

/**
 * Vue: Carousel slide (carousel-slide)
 * -----------------------------------------------------------------------------
 * A full screen element which houses a carousel and additional copy.
 *
 * @param {String} blocks.ctaText - Slide CTA text.
 * @param {String} blocks.ctaUrl - Slide CTA url.
 * @param {String} blocks.styles - Slide style customization values.
 * @param {String} blocks.subtitle - Slide subtitle text.
 * @param {String} blocks.text - Slide body text.
 * @param {String} blocks.title - Slide title text.
 * @param {String} layout - Section layout.
 *
 */
import { mapActions, mapState } from 'vuex'

import Btn from '~global/btn/btn'
import ResponsiveImage from '~global/images/responsive-image'
import VideoPlayer from '~global/video-player/video-player'

export default {
  name: 'CarouselSlide',

  components: {
    Btn,
    ResponsiveImage,
    VideoPlayer,
  },

  props: {
    block: {
      type: Object,
      default: () => ({
        ctaText: '',
        ctaUrl: '',
        desktopImage: '',
        desktopVideo: '',
        mobileImage: '',
        mobileVideo: '',
        subtitle: '',
        text: '',
        title: '',
        videoControls: '',
      }),
    },
    layout: {
      type: String,
      default: '',
    },
  },

  emits: {
    'carousel-image-loaded': () => true,
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Indicates if there is a video for that screen size.
     * @returns {Boolean}
     */
    hasAvailableVideo() {
      if (this.isMobile) {
        return Boolean(this.block.videoControls?.videoEnable && this.block.mobileVideo)
      }

      return Boolean(this.block.videoControls?.videoEnable && this.block.desktopVideo)
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
    }),
  },
}
</script>
